"use client";

import { useImageLoading } from "../../../../helpers/ImageLoadingContext";
import { useTextDirectionContext } from "../../../../helpers/TextDirectionContext";
import { getContentfulVideoUrl } from "../../../../helpers/contentful/getContentfulVideoUrl";
import { getContentfulImageUrl } from "../../../../helpers/contentful/images/getContentfulImageUrl";
import { getImageSource } from "../../../../helpers/getImageSource";
import { getStoryblokImageUrl } from "../../../../helpers/images/getStoryblokImageUrl";
import { getStoryblokVideoUrl } from "../../../../helpers/images/getStoryblokVideoUrl";
import { useObserveElementRef } from "../../../../helpers/in-viewport/in-viewport";
import { notNull } from "../../../../helpers/notNull";
import { notNullish } from "../../../../helpers/notNullish";
import { responsiveImageHelper } from "../../../../helpers/responsiveImageHelper";
import { useCardStackState } from "../../../../helpers/useCardStackState";
import { CardStack } from "../../../card-stack/card-stack";
import { LifecycleCard } from "../../../card-stack/lifecycle-card/lifecycle-card";
import { Kicker } from "../../../kicker/kicker";
import { Prose } from "../../../prose/prose";
import {
  LifecyclePhaseType,
  PhaseImageType,
  PhasePanelColumnsType,
} from "../lifecycle.types";
import { PhaseSideImageColumns } from "./PhaseSideImageColumns";
import { CosmosButton, CosmosIconChevronDown } from "@cosmos/web-react";
import classNames from "classnames/bind";
import { useEffect, useState } from "react";
import styles from "./lifecycle-phase.module.css";

const EVERY_SECOND_ITEM = 2;
const cx = classNames.bind(styles);

export const LifecyclePhase = ({
  phase,
  characterVideoUrl,
  index,
  onEnterViewport,
}: {
  phase: LifecyclePhaseType;
  characterVideoUrl: string | null;
  index: number;
  onEnterViewport: (phaseId: string) => void;
}) => {
  const textDirection = useTextDirectionContext();
  const [isActive, setIsActive] = useState<boolean>(false);

  const { ref, hasIntersected, isIntersecting } =
    useObserveElementRef<HTMLDivElement>({
      threshold: 0,
      rootMargin: "-50% 0px",
    });

  useEffect(() => {
    if (isIntersecting) {
      onEnterViewport(phase.id);
    }
  }, [isIntersecting, onEnterViewport, phase.id]);

  const { cardStackState, cardStackActions } = useCardStackState({
    rtl: textDirection === "rtl",
  });

  const characterVideoSource = characterVideoUrl
    ? getImageSource(characterVideoUrl)
    : null;

  return (
    <div
      className={cx("container", {
        "container--silver-bg": index % EVERY_SECOND_ITEM !== 0,
      })}
      id={phase.title?.toLowerCase()}
      ref={ref}
    >
      <div className={cx("inner")}>
        <section className={cx("content")}>
          {phase.title && (
            <h2 className={cx("title")}>
              <Kicker kind="bare" text={phase.title} />
            </h2>
          )}
          {characterVideoUrl && hasIntersected && (
            <div
              className={cx(
                "video-wrapper",
                {
                  "video-wrapper--gradient-grey":
                    index % EVERY_SECOND_ITEM !== 0,
                },
                {
                  "video-wrapper--gradient-white":
                    index % EVERY_SECOND_ITEM === 0,
                },
              )}
            >
              {characterVideoUrl &&
                (() => {
                  const videoProps = {
                    className: cx("video"),
                    preload: "auto",
                    autoPlay: true,
                    playsInline: true,
                    muted: true,
                    loop: true,
                  };

                  switch (characterVideoSource) {
                    case "contentful": {
                      return (
                        <video
                          {...videoProps}
                          src={getContentfulVideoUrl(characterVideoUrl)}
                        />
                      );
                    }
                    case "storyblok": {
                      return (
                        <video
                          {...videoProps}
                          src={getStoryblokVideoUrl(characterVideoUrl)}
                        />
                      );
                    }
                    default: {
                      return <video {...videoProps} src={characterVideoUrl} />;
                    }
                  }
                })()}
            </div>
          )}
          <div
            className={cx("facts-wrapper", {
              "facts-wrapper--no-asset": !characterVideoUrl,
            })}
          >
            <p
              className={cx("quote")}
              data-align={index % EVERY_SECOND_ITEM ? "end" : "start"}
              data-width={phase.cardsCollection?.length ? "default" : "full"}
            >
              {phase.quote}
            </p>

            {phase.cardsCollection && phase.cardsCollection.length > 0 && (
              <div
                className={cx("cards-stack-wrapper", {
                  "container--align-left lifecycle-phase--flex-start":
                    index % EVERY_SECOND_ITEM,
                })}
                data-align={index % EVERY_SECOND_ITEM ? "start" : "end"}
              >
                <div className={cx("card-stack-inner-wrapper")}>
                  <CardStack
                    cards={phase.cardsCollection}
                    cardStackState={cardStackState}
                    cardStackActions={cardStackActions}
                    hasNavigationDots={true}
                    renderCard={({ card }) => (
                      <LifecycleCard
                        smallBodyText={card.smallBodyText}
                        largeText={card.largeText}
                        kicker={phase.cardsHeadline ?? null}
                      />
                    )}
                  />
                </div>
              </div>
            )}

            <div
              className={cx("read-more-link-wrapper")}
              data-active={isActive ? "true" : "false"}
              data-align={index % EVERY_SECOND_ITEM ? "end" : "start"}
              data-width={phase.cardsCollection?.length ? "default" : "full"}
            >
              {/* `aria-controls` does not get passed down to Shadow DOM */}
              <CosmosButton
                className={cx("read-more-link")}
                aria-expanded={isActive}
                aria-controls={phase.id}
                kind="link"
                icon-placement="after"
                onClick={() => setIsActive((current) => !current)}
              >
                <CosmosIconChevronDown
                  slot="icon"
                  className={cx("read-more-link-icon")}
                />
                {isActive ? phase.readLessText : phase.readMoreText}
              </CosmosButton>
            </div>
            <div className={cx("hidden-content")} hidden={!isActive}>
              <div className={cx("hidden-content-inner")}>
                <div className={cx("hidden-content-inner-margin")}>
                  {phase.phaseBlocksCollection
                    ?.filter(notNull)
                    .map((item, index) => {
                      switch (item.__typename) {
                        case "PhaseText":
                          return (
                            <div
                              key={index}
                              className={cx("text-intro-wrapper")}
                            >
                              <Prose className={cx("text-intro")}>
                                {typeof item.text === "string" ? (
                                  <div
                                    style={{ display: "contents" }}
                                    dangerouslySetInnerHTML={{
                                      __html: item.text,
                                    }}
                                  />
                                ) : (
                                  item.text
                                )}
                              </Prose>
                            </div>
                          );
                        case "PhasePanelColumns":
                          return (
                            <PhasePanelColumns
                              key={index}
                              kicker={item.kicker}
                              id={item.id}
                              panelsCollection={item.panelsCollection}
                            />
                          );
                        case "PhaseImage":
                          return <PhaseImage key={index} item={item} />;
                        case "PhaseSideImageColumns":
                          return (
                            <PhaseSideImageColumns
                              key={index}
                              items={
                                item.sideImageColumnsCollection?.filter(
                                  notNullish,
                                ) ?? []
                              }
                            />
                          );
                        default:
                          return null;
                      }
                    })}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

function PhasePanelColumns({
  kicker,
  panelsCollection,
}: Omit<PhasePanelColumnsType, "__typename">) {
  return (
    <div className={cx("panel-column-wrapper")}>
      {kicker && (
        <span className={cx("kicker-wrapper")}>
          <Kicker kind="underlined" text={kicker} />
        </span>
      )}
      <div className={cx("panel-column-content")}>
        {panelsCollection?.filter(notNull).map((panel, index) => {
          const imageUrl = panel.image?.url;
          const imageSource = imageUrl ? getImageSource(imageUrl) : null;

          if (!panel.image || !imageUrl) {
            return null;
          }

          return (
            <div key={index} className={cx("panel-column")}>
              {(() => {
                const commonImageAttributes = {
                  className: cx("panel-column-image"),
                  alt: panel.image?.description ?? undefined,
                  sizes: "300px",
                };

                switch (imageSource) {
                  case "contentful":
                    return (
                      <img
                        src={getContentfulImageUrl(imageUrl, {})}
                        srcSet={[300, 450, 600]
                          .map((width) => {
                            const src = getContentfulImageUrl(imageUrl, {
                              width,
                              fm: "webp",
                            });
                            return `${src} ${width}w`;
                          })
                          .join(", ")}
                        {...commonImageAttributes}
                      />
                    );
                  case "storyblok":
                    return (
                      <img
                        {...commonImageAttributes}
                        src={getStoryblokImageUrl(imageUrl)}
                        srcSet={[300, 450, 600]
                          .map((width) => {
                            const src = getStoryblokImageUrl(imageUrl, {
                              width,
                              format: "webp",
                            });
                            return `${src} ${width}w`;
                          })
                          .join(", ")}
                      />
                    );
                  default:
                    return <img src={imageUrl} {...commonImageAttributes} />;
                }
              })()}
              <div className={cx("panel-column-card")}>
                <h3 className={cx("panel-column-headline")}>
                  {panel.headline}
                </h3>
                <div className={cx("panel-column-text")}>{panel.panelText}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PhaseImage({ item }: { item: PhaseImageType }) {
  const imageLoading = useImageLoading();
  const imageUrl = item.image?.url;
  const imageSource = imageUrl ? getImageSource(imageUrl) : null;

  const srcSet =
    imageUrl && imageSource
      ? {
          contentful: responsiveImageHelper.createContentfulSrcSet(imageUrl),
          storyblok: responsiveImageHelper.createStoryblokSrcSet(imageUrl),
          crepo: undefined, // unsupported
          fallback: undefined, // unsupported
        }[imageSource]
      : undefined;

  return (
    <div className={cx("phase-image-wrapper")}>
      {item.kicker && (
        <span className={cx("kicker-wrapper")}>
          <Kicker kind="underlined" text={item.kicker} />
        </span>
      )}
      {item.image?.url && (
        <img
          loading={imageLoading}
          srcSet={srcSet}
          sizes="min(680px, 100vw)"
          className={cx("phase-image")}
          alt={item.image.description ?? ""}
        />
      )}
    </div>
  );
}
